import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { supabaseClient } from "../utils/supabase";
import { useAuth } from "../hooks/useAuth";
import { glucoseCopy, isGlucoseLocale, type GlucoseCopy, type GlucoseLocale } from "./copy";

const STORAGE_KEY = "glucose_locale";

function detectLocale(): GlucoseLocale {
    if (typeof window === "undefined") return "en";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isGlucoseLocale(stored)) return stored;
    const language = window.navigator.language?.toLowerCase() ?? "";
    return language.startsWith("fr") ? "fr" : "en";
}

function applyLocale(locale: GlucoseLocale) {
    dayjs.locale(locale === "fr" ? "fr" : "en");
    if (typeof document !== "undefined") {
        document.documentElement.lang = locale === "fr" ? "fr" : "en";
    }
}

interface GlucoseLocaleContextValue {
    locale: GlucoseLocale;
    copy: GlucoseCopy;
    setLocale: (locale: GlucoseLocale) => void;
}

const GlucoseLocaleContext = createContext<GlucoseLocaleContextValue | null>(null);

export function GlucoseLocaleProvider({ children }: { children: ReactNode }) {
    const [locale, setLocaleState] = useState<GlucoseLocale>(detectLocale);
    const { user, profile, setAuthData } = useAuth();
    const syncedProfileId = useRef<string | null>(null);

    useEffect(() => {
        applyLocale(locale);
    }, [locale]);

    useEffect(() => {
        const preferred = profile?.preferred_locale;
        if (!profile?.profile_id || !isGlucoseLocale(preferred)) return;
        if (syncedProfileId.current === profile.profile_id) return;
        syncedProfileId.current = profile.profile_id;
        setLocaleState(preferred);
        window.localStorage.setItem(STORAGE_KEY, preferred);
    }, [profile]);

    const setLocale = (next: GlucoseLocale) => {
        setLocaleState(next);
        window.localStorage.setItem(STORAGE_KEY, next);
        if (!user || !profile) return;
        void supabaseClient
            .from("profiles")
            .update({ preferred_locale: next })
            .eq("profile_id", user.id)
            .then(({ error }) => {
                if (error) {
                    console.error("Failed to save language preference", error);
                    return;
                }
                setAuthData(user, { ...profile, preferred_locale: next });
            });
    };

    const value = useMemo(
        () => ({
            locale,
            copy: glucoseCopy[locale],
            setLocale,
        }),
        [locale, user, profile]
    );

    return <GlucoseLocaleContext.Provider value={value}>{children}</GlucoseLocaleContext.Provider>;
}

export function useGlucoseLocale() {
    const context = useContext(GlucoseLocaleContext);
    if (!context) {
        throw new Error("useGlucoseLocale must be used within GlucoseLocaleProvider");
    }
    return context;
}

export function useCopy() {
    return useGlucoseLocale().copy;
}

export function LanguageSwitch({ className }: { className?: string }) {
    const { locale, setLocale, copy } = useGlucoseLocale();
    return (
        <div className={className ?? "flex justify-end gap-2"} role="group" aria-label={copy.languageLabel}>
            <button
                type="button"
                aria-pressed={locale === "en"}
                onClick={() => setLocale("en")}
                className={
                    locale === "en"
                        ? "rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white"
                        : "rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100"
                }
            >
                {copy.english}
            </button>
            <button
                type="button"
                aria-pressed={locale === "fr"}
                onClick={() => setLocale("fr")}
                className={
                    locale === "fr"
                        ? "rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white"
                        : "rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100"
                }
            >
                {copy.french}
            </button>
        </div>
    );
}

export function readStoredGlucoseLocale(): GlucoseLocale {
    return detectLocale();
}
